import { ChevronRight, ListTree, Plus, Trash2, UserCheck } from 'lucide-preact'
import { type ComponentChildren, Fragment } from 'preact'
import { useState } from 'preact/hooks'
import { computed, effect } from '@preact/signals'
import { api, type ApiOutput } from '../lib/api.ts'
import { groupScopes } from '../lib/scope-groups.ts'
import { ReferenceBadge } from '../ui/reference-badge.tsx'
import { ErrorWithData } from '@01edu/api-client'
import { A, navigate, url } from '@01edu/signal-router'
import { isAdmin, onEvent, projectOwners } from '../lib/session.ts'
import { DialogModal } from '../ui/dialog.tsx'
import { notify } from '../ui/notifications.tsx'
import {
  type CoverageStatus,
  getLicenseStatus,
  type LicenseStatus,
  STATUS,
} from '../lib/status.ts'
import { Meter, MeterLegend } from '../ui/meter.tsx'

type ProjectOwner = ApiOutput['GET/api/project/owners'][number]
type ScopeOverview = ApiOutput['GET/api/project/scopes-overview'][number]
type License = ApiOutput['GET/api/project/licenses']['licenses'][number]

const revokeProjectOwner = api['DELETE/api/project/owner'].signal()
const inviteOwner = api['POST/api/invite-owner'].signal()
const projectScopesOverview = api['GET/api/project/scopes-overview'].signal()
const projectLicenses = api['GET/api/project/licenses'].signal()
const projectStats = api['GET/api/project/stats'].signal()

const EMPTY_STATS = {
  total: 0,
  active: 0,
  expired: 0,
  unlicensed: 0,
  seatsUsed: 0,
  available: 0,
  overused: 0,
}

const scopeStatuses = computed(() => {
  const allScopes = projectScopesOverview.data || []
  const licenses = projectLicenses.data?.licenses || []

  const statuses = new Map<string, CoverageStatus>(
    allScopes.map((scope) => [scope.path, 'none']),
  )

  for (const license of licenses) {
    const licenseStatus = getLicenseStatus(
      license.licenseStartAt,
      license.licenseEndAt,
    )

    for (const scopePath of license.licenseScopes) {
      const currentStatus = statuses.get(scopePath)

      if (!currentStatus || currentStatus === 'none') {
        statuses.set(scopePath, licenseStatus)
        continue
      }

      if (licenseStatus === 'active') {
        statuses.set(scopePath, 'active')
        continue
      }

      if (licenseStatus === 'expired' && currentStatus === 'coming') {
        statuses.set(scopePath, 'expired')
      }
    }
  }

  return statuses
})

const scopeLicenses = computed(() => {
  const licenses = projectLicenses.data?.licenses || []
  const licensesByScopePath = new Map<
    string,
    Array<{ id: number; status: LicenseStatus }>
  >()

  for (const license of licenses) {
    const status = getLicenseStatus(
      license.licenseStartAt,
      license.licenseEndAt,
    )
    for (const scopePath of license.licenseScopes) {
      const list = licensesByScopePath.get(scopePath)
      if (list) {
        list.push({ id: license.licenseId, status })
      } else {
        licensesByScopePath.set(scopePath, [{ id: license.licenseId, status }])
      }
    }
  }

  return licensesByScopePath
})

const overviewStats = computed(() => projectStats.data ?? EMPTY_STATS)

const filteredScopes = computed(() => {
  const allScopes = projectScopesOverview.data || []
  const status = url.params['scope-status']
  if (!status) return allScopes
  return allScopes.filter((scope) =>
    scopeStatuses.value.get(scope.path) === status
  )
})

const refreshProjectOverview = () => {
  const projectId = url.params.project
  if (!projectId) return
  projectScopesOverview.fetch({ project: projectId })
  projectLicenses.fetch({ project: projectId })
  projectStats.fetch({ project: projectId })
}

effect(refreshProjectOverview)

// SYNC_SUCCEEDED carries no project, so it is broadcast to every client for
// every instance synced: a sync wave delivers one event per project and would
// otherwise refetch all three endpoints that many times. GRANT_ACCESS would be
// the precise signal but has no trigger, and would be one event per
// participant.
let queuedRefresh: ReturnType<typeof setTimeout> | undefined
const queueProjectOverviewRefresh = () => {
  clearTimeout(queuedRefresh)
  queuedRefresh = setTimeout(refreshProjectOverview, 500)
}

onEvent(
  ['LICENSE_CREATED', 'LICENSE_UPDATED', 'LICENSE_DELETED', 'SYNC_SUCCEEDED'],
  queueProjectOverviewRefresh,
)

const errorReported = computed(() => {
  const { error } = inviteOwner
  if (!(error instanceof ErrorWithData)) return false
  const input = document.querySelector(
    `#invite-owner input[name=${error.data.input}]`,
  )
  if (!(input instanceof HTMLInputElement)) return false
  input.setCustomValidity(error.message)
  input.reportValidity()
  return true
})

const clearValidity = (e: Event) =>
  (e.target as HTMLInputElement).setCustomValidity('')

const handleInviteOwner = async (e: Event) => {
  e.preventDefault()
  const form = e.target as HTMLFormElement
  const ownerEmailInput = form.ownerEmail as HTMLInputElement
  const ownerFullNameInput = form.ownerFullName as HTMLInputElement

  const projectId = url.params.project
  if (!projectId) return

  await inviteOwner.fetch({
    email: ownerEmailInput.value,
    fullName: ownerFullNameInput.value,
    project: projectId,
  })

  if (inviteOwner.error) {
    return notify({
      status: 'failure',
      description: inviteOwner.error.message,
    })
  }

  notify({
    status: 'success',
    description: `Invitation sent to ${ownerEmailInput.value}`,
  })

  form.reset()
  navigate({ params: { dialog: null }, replace: true })

  projectOwners.fetch({ project: projectId })
}

const handleConfirmRevoke = async () => {
  const projectId = url.params.project
  const revokeUserId = Number(url.params['revoke-owner'])

  if (!revokeUserId) return

  await revokeProjectOwner.fetch({
    userId: revokeUserId,
    projectId: Number(projectId),
  })

  if (!revokeProjectOwner.error) {
    projectOwners.fetch({ project: projectId || '' })
  }

  navigate({ params: { dialog: null, 'revoke-owner': null }, replace: true })
}

const InviteOwnerModal = () => (
  <DialogModal id='invite-owner'>
    <h3 class='text-lg font-bold'>Invite Project Owner</h3>
    <form onSubmit={handleInviteOwner} class='py-4 fieldset gap-4'>
      <label class='form-control w-80'>
        <div class='label'>
          <span class='label-text font-semibold'>Owner Email</span>
        </div>
        <input
          name='ownerEmail'
          type='email'
          minLength={4}
          onInput={clearValidity}
          placeholder='email@example.com'
          class='input input-md w-full'
          required
        />
      </label>
      <label class='form-control w-80'>
        <div class='label'>
          <span class='label-text font-semibold'>Owner Full Name</span>
        </div>
        <input
          name='ownerFullName'
          minLength={4}
          onInput={clearValidity}
          placeholder='John Doe'
          class='input input-md w-full'
          required
        />
      </label>

      <button
        type='submit'
        class='btn btn-primary w-full'
        disabled={!!inviteOwner.pending}
      >
        {inviteOwner.pending ? 'Sending...' : 'Send Invitation'}
      </button>

      {!errorReported.value && inviteOwner.error && (
        <div class='alert alert-error'>
          <span class='text-sm'>{inviteOwner.error.message}</span>
        </div>
      )}
    </form>
  </DialogModal>
)

const RevokeOwnerModal = () => {
  const owners = projectOwners.data || []
  const revokeUserId = Number(url.params['revoke-owner'])
  const ownerToRevoke = revokeUserId
    ? owners.find((o) => o.userId === revokeUserId)
    : null

  return (
    <DialogModal id='revoke-owner'>
      <h3 class='text-lg font-bold'>Confirm Revoke Access</h3>
      {ownerToRevoke && (
        <>
          <p class='py-4'>
            Remove <strong>{ownerToRevoke.userFullName}</strong>{' '}
            as a project owner?
          </p>
          <p class='text-sm text-base-content/70 pb-4'>
            They will lose access to this project and be logged out of all
            sessions.
          </p>
        </>
      )}

      {revokeProjectOwner.error && (
        <div class='alert alert-error mb-4'>
          <span class='text-sm'>
            {revokeProjectOwner.error.message}
          </span>
        </div>
      )}

      <div class='flex gap-2 justify-end'>
        <A
          class='btn btn-ghost'
          params={{ dialog: null, 'revoke-owner': null }}
          replace
        >
          Cancel
        </A>
        <button
          type='button'
          class='btn btn-error'
          disabled={!!revokeProjectOwner.pending}
          onClick={handleConfirmRevoke}
        >
          {revokeProjectOwner.pending ? 'Revoking...' : 'Revoke Access'}
        </button>
      </div>
    </DialogModal>
  )
}

const ProjectOwnersCard = () => {
  const owners = projectOwners.data || []
  const loading = projectOwners.pending
  const error = projectOwners.error

  return (
    <div class='bg-base-100 border border-base-300 rounded-box overflow-hidden'>
      <div class='p-4 border-b border-base-300 flex items-center justify-between'>
        <h2 class='card-title font-bold '>
          Project Owners
        </h2>
        {isAdmin.value && (
          <A
            class='btn btn-primary btn-sm'
            params={{ dialog: 'invite-owner' }}
            replace
          >
            <Plus class='h-5 w-5' />
            Add Owner
          </A>
        )}
      </div>

      {loading && (
        <div class='flex justify-center py-8'>
          <span class='loading loading-spinner loading-lg'></span>
        </div>
      )}

      {error && (
        <div class='alert alert-error'>
          <span>{error.message}</span>
        </div>
      )}

      {!loading && !error && owners.length === 0 && (
        <div class='text-center py-8 text-base-content/60'>
          <UserCheck class='h-12 w-12 mx-auto mb-4 opacity-50' />
          <p>No owners found for this project.</p>
        </div>
      )}

      {!loading && !error && owners.length > 0 && (
        <div class='overflow-x-auto overflow-y-auto max-h-64'>
          <table class='w-full text-left border-collapse'>
            <thead>
              <tr class='bg-base-100 border-b border-base-300 sticky top-0 z-10'>
                <th class='px-6 py-3 text-xs font-semibold text-base-content/60 uppercase tracking-wider'>
                  Owner
                </th>
                <th class='px-6 py-3 text-xs font-semibold text-base-content/60 uppercase tracking-wider'>
                  Email Address
                </th>
                <th class='px-6 py-3 text-xs font-semibold text-base-content/60 uppercase tracking-wider'>
                  Added On
                </th>
                {isAdmin.value && (
                  <th class='px-6 py-3 text-xs font-semibold text-base-content/60 uppercase tracking-wider text-right'>
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody class='divide-y divide-base-300'>
              {owners.map((owner: ProjectOwner) => (
                <tr
                  key={owner.userId}
                  class='border-b border-base-300 hover:bg-base-300/20'
                >
                  <td class='px-6 py-4'>
                    <div class='flex items-center gap-3'>
                      <div class='size-10 bg-base-300 rounded-full overflow-hidden ring-2 ring-base-300 flex items-center justify-center text-sm font-bold text-base-content'>
                        {owner.userFullName.split(' ').map((n) => n[0]).join('')
                          .toUpperCase()}
                      </div>
                      <div class='flex flex-col'>
                        <span class='text-sm font-bold text-base-content'>
                          {owner.userFullName}
                        </span>
                        <span class='text-xs text-base-content/60'>
                          Project Owner
                        </span>
                      </div>
                    </div>
                  </td>
                  <td class='px-6 py-4 text-sm text-base-content/70'>
                    {owner.userEmail}
                  </td>
                  <td class='px-6 py-4 text-sm text-base-content/70'>
                    {new Date(owner.createdAt * 1000).toLocaleDateString()}
                  </td>
                  {isAdmin.value && (
                    <td class='px-6 py-4 text-right'>
                      <A
                        class='btn btn-sm btn-outline btn-error gap-1'
                        params={{
                          dialog: 'revoke-owner',
                          'revoke-owner': owner.userId,
                        }}
                        replace
                      >
                        <Trash2 class='h-4 w-4' />
                        Revoke
                      </A>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin.value && <InviteOwnerModal />}
      {isAdmin.value && <RevokeOwnerModal />}
    </div>
  )
}

type FilterChipProps = {
  label: string
  value: number
  textColor: string
  description: string
  status: string | null
}

// Bordered to read as clickable, unlike the plain-text seat values.
const FilterChip = (
  { label, value, textColor, description, status }: FilterChipProps,
) => {
  const currentStatus = url.params['scope-status']
  const isActive = currentStatus === status ||
    (!currentStatus && status === null)

  return (
    <A
      params={{ 'scope-status': status }}
      title={description}
      class={`rounded-field border px-3 py-1.5 flex flex-wrap justify-center items-baseline gap-x-1.5 text-center transition-colors ${
        isActive
          ? 'border-base-content/20 bg-base-200'
          : 'border-base-300 hover:bg-base-200'
      }`}
    >
      <span class={`${textColor} text-lg font-bold`}>{value}</span>
      <span class='text-base-content/60 text-xs font-semibold uppercase tracking-wider'>
        {label}
      </span>
    </A>
  )
}

const STAT_CONFIGS = [
  {
    status: null,
    label: 'Total',
    key: 'total',
    textColor: 'text-primary',
    description: 'All participants with access to a project scope',
  },
  {
    status: 'expired',
    label: 'Expired',
    key: 'expired',
    textColor: 'text-error',
    description: 'Participants whose only license coverage has expired',
  },
  {
    status: 'none',
    label: 'No license',
    key: 'unlicensed',
    textColor: 'text-base-content/70',
    description: 'Participants with scope access but no license',
  },
] as const

const seatSegments = computed(() => {
  const { seatsUsed, available, overused } = overviewStats.value
  return [
    { value: seatsUsed, bg: 'bg-primary', label: 'Used' },
    { value: available, bg: 'bg-success', label: 'Available' },
    { value: overused, bg: 'bg-error', label: 'Overused' },
  ]
})

const StatCard = (
  { label, children }: { label: string; children: ComponentChildren },
) => (
  <div class='bg-base-100 border border-base-300 rounded-box p-4 flex flex-col gap-3'>
    <span class='text-base-content/70 text-sm font-semibold whitespace-nowrap'>
      {label}
    </span>
    {children}
  </div>
)

const StatsBar = () => (
  <div class='grid gap-4 lg:grid-cols-2'>
    <StatCard label='Participants'>
      <div class='grid grid-cols-3 gap-2 flex-1'>
        {STAT_CONFIGS.map(({ key, ...config }) => (
          <FilterChip
            key={key}
            value={overviewStats.value[key]}
            {...config}
          />
        ))}
      </div>
    </StatCard>
    <StatCard label='Seats (active licenses)'>
      <Meter segments={seatSegments.value} height='h-3' />
      <MeterLegend
        segments={seatSegments.value}
        layout='grid grid-cols-3 gap-2'
      />
    </StatCard>
  </div>
)

const ScopeRow = ({ scope }: { scope: ScopeOverview }) => {
  const licenses = scopeLicenses.value.get(scope.path) || []
  return (
    <tr class='border-b border-base-300 hover:bg-base-300/20'>
      <td class='px-6 py-4'>
        <div class='flex items-center gap-3 pl-8'>
          <ListTree class='h-5 w-5 text-primary' />
          <span
            class='text-sm font-medium text-base-content'
            title={scope.objectName ?? undefined}
          >
            {scope.path}
          </span>
        </div>
      </td>
      <td class='px-6 py-4'>
        <div class='flex items-center justify-center gap-2'>
          <span class='text-sm font-medium text-base-content'>
            {scope.participantCount}
          </span>
        </div>
      </td>
      <td class='px-6 py-4'>
        {licenses.length > 0
          ? (
            <div class='flex gap-2 flex-wrap'>
              {licenses.map((license) => (
                <A
                  key={license.id}
                  params={{ license: license.id }}
                  class={`badge badge-sm ${STATUS[license.status].badge}`}
                >
                  #{license.id}
                </A>
              ))}
            </div>
          )
          : <span class='text-base-content/50 text-sm'>None</span>}
      </td>
    </tr>
  )
}

const ProjectScopesCard = () => {
  const scopes = filteredScopes.value
  const groups = groupScopes(scopes)
  const [expanded, setExpanded] = useState<string[]>([])
  const loading = projectScopesOverview.pending
  const error = projectScopesOverview.error

  const toggleGroup = (key: string) =>
    setExpanded((keys) =>
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]
    )

  return (
    <div class='bg-base-100 shadow-sm border border-base-300 rounded-box overflow-hidden'>
      <div class='p-4 border-b border-base-300'>
        <h2 class='card-title font-bold'>
          Project Scopes
        </h2>
      </div>

      {loading && (
        <div class='flex justify-center py-12'>
          <span class='loading loading-spinner loading-lg'></span>
        </div>
      )}

      {error && (
        <div class='alert alert-error m-4'>
          <span>{error.message}</span>
        </div>
      )}

      {!loading && !error && scopes.length === 0 && (
        <div class='text-center py-12 text-base-content/50'>
          <ListTree class='h-12 w-12 mx-auto mb-4 opacity-50' />
          <p>No scopes found for this project.</p>
        </div>
      )}

      {!loading && !error && scopes.length > 0 && (
        <div class='overflow-x-auto overflow-y-auto max-h-64'>
          <table class='w-full text-left border-collapse'>
            <thead>
              <tr class='bg-base-100 border-b border-base-300 sticky top-0 z-10'>
                <th class='px-6 py-3 text-xs font-semibold text-base-content/60 uppercase tracking-wider'>
                  Scope Path
                </th>
                <th class='px-6 py-3 text-xs font-semibold text-base-content/60 uppercase tracking-wider text-center'>
                  Participants
                </th>
                <th class='px-6 py-3 text-xs font-semibold text-base-content/60 uppercase tracking-wider'>
                  Coverage
                </th>
              </tr>
            </thead>
            <tbody class='divide-y divide-base-300'>
              {groups.map((group) => {
                const isExpanded = groups.length === 1 ||
                  expanded.includes(group.key)
                const participantTotal = group.scopes.reduce(
                  (sum, scope) => sum + scope.participantCount,
                  0,
                )
                const licensedCount = group.scopes.filter(
                  (scope) => scope.usedInLicenses,
                ).length
                return (
                  <Fragment key={group.key}>
                    <tr
                      class='bg-base-200/50 border-b border-base-300 hover:bg-base-200 cursor-pointer'
                      onClick={() => toggleGroup(group.key)}
                    >
                      <td class='px-6 py-3'>
                        <div class='flex items-center gap-3'>
                          <ChevronRight
                            class={[
                              'h-4 w-4 transition-transform',
                              isExpanded ? 'rotate-90' : '',
                            ].join(' ')}
                          />
                          <ReferenceBadge referencePath={group.referencePath} />
                          <span class='text-sm font-semibold text-base-content'>
                            {group.name}
                          </span>
                          <span class='badge badge-ghost badge-sm'>
                            {group.scopes.length}
                          </span>
                        </div>
                      </td>
                      <td class='px-6 py-3 text-center text-sm font-semibold'>
                        {participantTotal}
                      </td>
                      <td class='px-6 py-3'>
                        <div class='flex items-center gap-3 w-32'>
                          <Meter
                            segments={[
                              {
                                value: licensedCount,
                                bg: STATUS.active.bg,
                                label: 'Licensed',
                              },
                              {
                                value: group.scopes.length - licensedCount,
                                bg: 'bg-base-300',
                                label: 'Uncovered',
                              },
                            ]}
                          />
                          <span class='text-xs text-base-content/60 whitespace-nowrap tabular-nums'>
                            {licensedCount}/{group.scopes.length}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {isExpanded &&
                      group.scopes.map((scope) => (
                        <ScopeRow key={scope.scopeId} scope={scope} />
                      ))}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export const ProjectOverviewSection = () => {
  const allScopes = projectScopesOverview.data
  return (
    <div class='flex flex-col gap-4'>
      {(allScopes && allScopes.length > 0) && <StatsBar />}
      <ProjectScopesCard />
      <ProjectOwnersCard />
    </div>
  )
}
